// @contexts/DocumentAssistantDictationClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import { requireContext } from "@contexts/requireContext";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import {
    DefaultDocumentAssistantDictationClient,
    type DocumentAssistantDictationClient,
} from "@services/clients/DocumentAssistantDictationClient";
import { StubDocumentAssistantDictationClient } from "@services/stubs/StubDocumentAssistantDictationClient";

const defaultStubDocumentAssistantDictationClient = new StubDocumentAssistantDictationClient();

const DocumentAssistantDictationClientContext = createContext<OptionTs<DocumentAssistantDictationClient>>(
    Option.none<DocumentAssistantDictationClient>(),
);

const shouldUseDefaultStub = (): boolean =>
    enabledFeatures[FeaturesEnum.StubClients] || import.meta.env.DEV || import.meta.env.MODE === "test";

type DocumentAssistantDictationClientProviderProps = {
    readonly children: ReactNode;
    readonly client: OptionTs<DocumentAssistantDictationClient>;
};

export const DocumentAssistantDictationClientProvider: React.FC<DocumentAssistantDictationClientProviderProps> = ({
    children,
    client: injectedClient,
}) => {
    const client = useMemo<DocumentAssistantDictationClient>(() => {
        return Option.fold(
            injectedClient,
            () =>
                shouldUseDefaultStub()
                    ? defaultStubDocumentAssistantDictationClient
                    : new DefaultDocumentAssistantDictationClient(),
            (value) => value,
        );
    }, [injectedClient]);

    const contextValue = useMemo(
        () => Option.some(client),
        [client],
    );

    return (
        <DocumentAssistantDictationClientContext.Provider value={contextValue}>
            {children}
        </DocumentAssistantDictationClientContext.Provider>
    );
};

export const useDocumentAssistantDictationClient = (): DocumentAssistantDictationClient => {
    return requireContext(
        useContext(DocumentAssistantDictationClientContext),
        "useDocumentAssistantDictationClient must be used within a DocumentAssistantDictationClientProvider",
    );
};
